import { useState, useCallback } from 'react';
import { Webhook, Loader2, Plus, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
} from '@flote/ui';
import { deliveryTsr } from '../../api';
import { getErrorMessage } from '../../lib/error-utils';
import type { CreateOnfleetWebhookRequest } from '@flote/contracts/http';

const WEBHOOK_TRIGGERS = {
  0: { name: 'Task Started', description: 'When a worker starts a task' },
  1: { name: 'Task ETA', description: 'When ETA is updated' },
  2: { name: 'Task Arrival', description: 'When worker arrives at destination' },
  3: { name: 'Task Completed', description: 'When a task is completed' },
  4: { name: 'Task Failed', description: 'When a task fails' },
  5: { name: 'Worker Duty', description: 'When worker goes on/off duty' },
  6: { name: 'Task Created', description: 'When a new task is created' },
  7: { name: 'Task Updated', description: 'When a task is updated' },
  8: { name: 'Task Deleted', description: 'When a task is deleted' },
  9: { name: 'Task Assigned', description: 'When a task is assigned to a worker' },
  10: { name: 'Task Unassigned', description: 'When a task is unassigned' },
  11: { name: 'Task Delayed', description: 'When a task is delayed' },
  12: { name: 'Task Cloned', description: 'When a task is cloned' },
  13: { name: 'SMS Response', description: 'When recipient responds to SMS' },
  14: { name: 'Worker Created', description: 'When a new worker is created' },
  15: { name: 'Worker Deleted', description: 'When a worker is deleted' },
} as const;

function getTriggerName(trigger: number): string {
  return WEBHOOK_TRIGGERS[trigger as keyof typeof WEBHOOK_TRIGGERS]?.name || `Trigger ${trigger}`;
}

function getTriggerBadgeColor(trigger: number): string {
  if ([3, 4].includes(trigger)) return 'bg-green-100 text-green-800';
  if ([5, 14, 15].includes(trigger)) return 'bg-purple-100 text-purple-800';
  if ([0, 1, 2].includes(trigger)) return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
}

function StatCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function WebhooksPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWebhookId, setSelectedWebhookId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Form state
  const [formUrl, setFormUrl] = useState('');
  const [formTrigger, setFormTrigger] = useState<string>('');
  const [formThreshold, setFormThreshold] = useState('');
  const [formName, setFormName] = useState('');

  // Fetch webhooks list
  const { data: webhooksResponse, isLoading: webhooksLoading, refetch: refetchWebhooks } = deliveryTsr.adminOnfleetWebhooks.list.useQuery({
    queryKey: ['onfleet-webhooks-list'],
  });
  const webhooks = webhooksResponse?.status === 200 ? webhooksResponse.body : [];

  // Mutations
  const createWebhookMutation = deliveryTsr.adminOnfleetWebhooks.create.useMutation();
  const deleteWebhookMutation = deliveryTsr.adminOnfleetWebhooks.delete.useMutation();

  const resetForm = useCallback(() => {
    setFormUrl('');
    setFormTrigger('');
    setFormThreshold('');
    setFormName('');
  }, []);

  const handleCreateWebhook = useCallback(async () => {
    if (!formUrl || formTrigger === '') {
      toast.error('URL and trigger are required');
      return;
    }

    setIsProcessing(true);
    try {
      const data: CreateOnfleetWebhookRequest = {
        url: formUrl,
        trigger: parseInt(formTrigger, 10),
        ...(formThreshold && { threshold: parseInt(formThreshold, 10) }),
        ...(formName && { name: formName }),
      };

      const response = await createWebhookMutation.mutateAsync({
        body: data,
      });

      if (response.status === 201) {
        toast.success('Webhook created successfully');
        setIsCreateModalOpen(false);
        resetForm();
        refetchWebhooks();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to create webhook');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create webhook'));
      console.error('Create webhook error:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [formUrl, formTrigger, formThreshold, formName, createWebhookMutation, refetchWebhooks, resetForm]);

  const handleDeleteWebhook = useCallback(async () => {
    if (!selectedWebhookId) return;

    setIsProcessing(true);
    try {
      const response = await deleteWebhookMutation.mutateAsync({
        params: { webhookId: selectedWebhookId },
        body: {},
      });

      if (response.status === 204) {
        toast.success('Webhook deleted successfully');
        setIsDeleteModalOpen(false);
        setSelectedWebhookId(null);
        refetchWebhooks();
      } else {
        const errorBody = response.body as unknown as { message?: string } | undefined;
        toast.error(errorBody?.message || 'Failed to delete webhook');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete webhook'));
      console.error('Delete webhook error:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedWebhookId, deleteWebhookMutation, refetchWebhooks]);

  const openDeleteModal = (webhookId: string) => {
    setSelectedWebhookId(webhookId);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Onfleet Webhooks</h1>
          <p className="text-muted-foreground">
            Manage Onfleet webhook subscriptions for event notifications
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Create Webhook
        </Button>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Webhooks are registered directly with Onfleet. Make sure your webhook URL is publicly accessible
          and can handle Onfleet's signature validation.
        </AlertDescription>
      </Alert>

      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Total Webhooks"
          count={webhooks.length}
          icon={Webhook}
          color="bg-blue-100 text-blue-600"
          isLoading={webhooksLoading}
        />
        <StatCard
          title="Task Events"
          count={webhooks.filter(w => [0, 1, 2, 3, 4, 6, 7, 8, 9, 10, 11, 12].includes(w.trigger)).length}
          icon={Webhook}
          color="bg-green-100 text-green-600"
          isLoading={webhooksLoading}
        />
        <StatCard
          title="Worker Events"
          count={webhooks.filter(w => [5, 14, 15].includes(w.trigger)).length}
          icon={Webhook}
          color="bg-purple-100 text-purple-600"
          isLoading={webhooksLoading}
        />
      </div>

      {/* Webhooks List */}
      <Card>
        <CardHeader>
          <CardTitle>Registered Webhooks</CardTitle>
        </CardHeader>
        <CardContent>
          {webhooksLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : webhooks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Webhook className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No webhooks registered</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                Create a webhook to receive real-time notifications when events occur in Onfleet.
              </p>
              <Button className="mt-4" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Webhook
              </Button>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead className="hidden md:table-cell">URL</TableHead>
                      <TableHead className="hidden sm:table-cell">Threshold</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhooks.map((webhook) => (
                      <TableRow key={webhook.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-blue-100 sm:flex">
                              <Webhook className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="font-medium">{webhook.name || `Webhook ${webhook.id.slice(0, 8)}`}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTriggerBadgeColor(webhook.trigger)}>
                            {getTriggerName(webhook.trigger)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden max-w-xs truncate md:table-cell">
                          <span className="text-muted-foreground font-mono text-sm">{webhook.url}</span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {webhook.threshold ? (
                            <span className="text-muted-foreground">{webhook.threshold}s</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteModal(webhook.id)}
                            className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Webhook Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={(open) => {
        setIsCreateModalOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Webhook</DialogTitle>
            <DialogDescription>
              Register a new webhook to receive Onfleet event notifications.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name (optional)</Label>
              <Input
                id="name"
                placeholder="My Webhook"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">Webhook URL *</Label>
              <Input
                id="url"
                placeholder="https://example.com/webhooks/onfleet"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trigger">Trigger Event *</Label>
              <Select value={formTrigger} onValueChange={setFormTrigger}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a trigger" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(WEBHOOK_TRIGGERS).map(([value, { name, description }]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex flex-col">
                        <span>{name}</span>
                        <span className="text-xs text-muted-foreground">{description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="threshold">Threshold (seconds, optional)</Label>
              <Input
                id="threshold"
                type="number"
                placeholder="e.g., 120"
                value={formThreshold}
                onChange={(e) => setFormThreshold(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                For ETA triggers, specify how many seconds before arrival to trigger the webhook.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleCreateWebhook} disabled={isProcessing || !formUrl || formTrigger === ''}>
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Webhook'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Webhook</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this webhook? This action cannot be undone.
              You will stop receiving notifications for this event.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteWebhook} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Webhook'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
